/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect } from "react";
import type { SetURLSearchParams } from 'react-router';

type UrlSyncArgs = {
  enableUrlNavigation: boolean;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  stepParamName: string;
  stepIdOrder: string[];
  currentStepId: string;
  lockedStepId?: string;
  setCurrentStepIdState: (stepId: string) => void;
};

/**
 * Replaces the step search param with the locked step id, using history replace.
 *
 * @param setSearchParams The router search-params setter.
 * @param stepParamName The query param name holding the step id.
 * @param lockedStepId The step id to force into the URL.
 */
function setLockedParam(
  setSearchParams: SetURLSearchParams,
  stepParamName: string,
  lockedStepId: string,
): void {
  setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set(stepParamName, lockedStepId);
    return next;
  }, { replace: true });
}

/**
 * Keeps the current step in sync with the URL search params, enforcing a
 * locked step when one is active.
 */
function enforceLocked(
  currentStepId: string, lockedStepId: string,
  setCurrentStepIdState: (id: string) => void,
  setSearchParams: SetURLSearchParams, stepParamName: string,
): void {
  if (currentStepId !== lockedStepId) {
    setCurrentStepIdState(lockedStepId);
    setLockedParam(setSearchParams, stepParamName, lockedStepId);
  }
}

/**
 * Determines whether a URL-provided step is unusable: missing, already current,
 * or not part of the known step order.
 *
 * @param urlStep The step id read from the URL, or null.
 * @param currentStepId The currently active step id.
 * @param stepIdOrder The ordered list of valid step ids.
 * @returns True when the URL step should be ignored.
 */
function isInvalidUrlStep(urlStep: string | null, currentStepId: string, stepIdOrder: string[]): boolean {
  return !urlStep || urlStep === currentStepId || !stepIdOrder.includes(urlStep);
}

/**
 * Applies a URL-driven step change: ignores invalid steps, re-locks the URL when
 * a lock is active, otherwise sets the current step from the URL.
 *
 * @param urlStep The step id read from the URL, or null.
 * @param currentStepId The currently active step id.
 * @param stepIdOrder The ordered list of valid step ids.
 * @param lockedStepId The locked step id, if any.
 * @param setCurrentStepIdState Setter for the active step id.
 * @param setSearchParams The router search-params setter.
 * @param stepParamName The query param name holding the step id.
 */
function navigateUrlStep(
  urlStep: string | null, currentStepId: string, stepIdOrder: string[],
  lockedStepId: string | undefined,
  setCurrentStepIdState: (id: string) => void,
  setSearchParams: SetURLSearchParams, stepParamName: string,
): void {
  if (isInvalidUrlStep(urlStep, currentStepId, stepIdOrder)) return;
  if (lockedStepId && urlStep !== lockedStepId) {
    setLockedParam(setSearchParams, stepParamName, lockedStepId);
    return;
  }
  setCurrentStepIdState(urlStep!);
}

/**
 * Effect hook that bidirectionally syncs the wizard's current step with the URL
 * search param, enforcing any locked step on each change.
 *
 * @param args URL-sync configuration (params, setters, step order, lock).
 */
export function useStepUrlSync({
  enableUrlNavigation, searchParams, setSearchParams,
  stepParamName, stepIdOrder, currentStepId, lockedStepId, setCurrentStepIdState,
}: UrlSyncArgs): void {
  useEffect(() => {
    if (!enableUrlNavigation) return;
    if (lockedStepId && stepIdOrder.includes(lockedStepId)) {
      enforceLocked(currentStepId, lockedStepId, setCurrentStepIdState, setSearchParams, stepParamName);
      return;
    }
    const urlStep = searchParams.get(stepParamName);
    navigateUrlStep(
      urlStep, currentStepId, stepIdOrder, lockedStepId,
      setCurrentStepIdState, setSearchParams, stepParamName,
    );
  }, [
    searchParams, stepParamName, enableUrlNavigation, stepIdOrder,
    currentStepId, lockedStepId, setSearchParams, setCurrentStepIdState,
  ]);
}
